import React, { useMemo, useState } from "react";
import { trpc } from "../main/components/trpc.ts";
import { DateTime } from "luxon";
import { Patients } from "cadendar-shared";

export const ImportedEmailsPage = () => {
  const {
    data: emails,
    refetch,
    isLoading,
  } = trpc.importedEmails.getPending.useQuery();
  const assignMutation = trpc.importedEmails.assignPatient.useMutation({
    onSuccess: () => refetch(),
  });
  const ignoreMutation = trpc.importedEmails.ignoreEmail.useMutation({
    onSuccess: () => refetch(),
  });

  if (isLoading) return <div className="p-4">Chargement...</div>;

  if (!emails || emails.length === 0)
    return <div className="p-4">Aucun email en attente d'assignation.</div>;

  return (
    <div className="p-4 flex flex-col gap-4 max-w-4xl mx-auto h-full overflow-y-auto">
      <h1 className="text-xl font-bold flex items-center gap-2">
        📥 Emails importés ({emails.length})
      </h1>
      <div className="flex flex-col gap-4">
        {emails.map((email) => (
          <EmailCard
            key={email.id}
            email={email}
            onAssign={(patientId) =>
              assignMutation.mutate({ emailId: email.id, patientId })
            }
            onIgnore={() => ignoreMutation.mutate({ emailId: email.id })}
          />
        ))}
      </div>
    </div>
  );
};

type ImportedEmailAttachment = {
  filename: string;
  content_type: string;
  s3_key: string;
  url?: string;
};

type ImportedEmail = {
  id: string;
  sender: string;
  subject: string;
  date: string | Date | null;
  body_text: string | null;
  body_html: string | null;
  attachments: ImportedEmailAttachment[];
  categories?: string[];
};

const EmailCard = ({
  email,
  onAssign,
  onIgnore,
}: {
  email: ImportedEmail;
  onAssign: (pid: string) => void;
  onIgnore: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patients | null>(null);

  const bodyPreview = useMemo(() => {
    if (email.body_text && email.body_text.trim().length > 0) {
      return email.body_text;
    }

    if (email.body_html && email.body_html.trim().length > 0) {
      const doc = new DOMParser().parseFromString(email.body_html, "text/html");
      return doc.body.textContent?.trim() || "Contenu vide";
    }

    return "Contenu vide";
  }, [email.body_html, email.body_text]);

  const { data: candidates } = trpc.patient.findByName.useQuery(
    { nameLookup: searchTerm },
    { enabled: searchTerm.length > 2 },
  );

  const handleAssign = () => {
    if (selectedPatient) {
      if (
        window.confirm(
          `Assigner cet email à ${selectedPatient.name} ${selectedPatient.forename} ?`,
        )
      ) {
        onAssign(selectedPatient.id);
      }
    }
  };

  return (
    <div className="border border-gray-200 p-4 rounded-lg bg-white shadow-sm flex flex-col gap-3">
      <div className="flex justify-between items-start border-b pb-2">
        <div>
          <div className="font-bold text-gray-800">
            {email.sender || "Expéditeur inconnu"}
          </div>
          <div className="text-sm font-medium text-gray-700">
            {email.subject || "(Sans objet)"}
          </div>
        </div>
        <div className="text-xs text-gray-500 whitespace-nowrap bg-gray-100 px-2 py-1 rounded">
          {email.date
            ? (typeof email.date === "string"
                ? DateTime.fromISO(email.date)
                : DateTime.fromJSDate(email.date)
              ).toFormat("dd/MM/yyyy HH:mm")
            : "Date inconnue"}
        </div>
      </div>

      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 max-h-40 overflow-y-auto whitespace-pre-wrap font-mono text-xs border">
        {bodyPreview}
      </div>

      <div className="text-xs text-gray-600">
        <div className="font-semibold mb-1">Catégories</div>
        {email.categories && email.categories.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {email.categories.map((cat) => (
              <span
                key={cat}
                className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100"
              >
                {cat}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-gray-400">Aucune catégorie.</div>
        )}
      </div>

      <div className="text-xs text-gray-600">
        <div className="font-semibold mb-1">Pièces jointes</div>
        {email.attachments.length > 0 ? (
          <ul className="list-disc pl-5 space-y-1">
            {email.attachments.map((att) => (
              <li key={att.s3_key} className="text-gray-700">
                <div className="flex items-center gap-2">
                  <span>
                    {att.filename}{" "}
                    <span className="text-gray-400">({att.content_type})</span>
                  </span>
                  {att.url ? (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Voir
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-gray-400">Aucune pièce jointe.</div>
        )}
      </div>

      <div className="mt-2 flex gap-3 items-end border-t pt-3">
        {!selectedPatient ? (
          <div className="relative grow max-w-md Group">
            <label className="text-xs font-bold mb-1 block text-gray-600">
              Rechercher un patient:
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 p-2 rounded text-sm focus:ring-2 focus:ring-blue-300 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tapez Nom ou Prénom..."
            />
            {candidates && candidates.length > 0 && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-300 shadow-lg z-10 max-h-60 overflow-auto rounded-b mt-1">
                {candidates.map((p: any) => (
                  <div
                    key={p.id}
                    className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-0 text-sm"
                    onClick={() => {
                      setSelectedPatient(p);
                      setSearchTerm("");
                    }}
                  >
                    <span className="font-bold">
                      {p.name} {p.forename}
                    </span>
                    <span className="text-gray-500 text-xs ml-2">
                      {p.birthdate}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grow flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
            <div className="text-green-700 font-medium">
              ✅ {selectedPatient.name} {selectedPatient.forename}
            </div>
            <button
              className="text-xs text-gray-500 hover:text-red-600 underline ml-2"
              onClick={() => setSelectedPatient(null)}
            >
              Changer
            </button>
          </div>
        )}

        <div className="flex gap-2 ml-auto">
          <button
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              selectedPatient
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            disabled={!selectedPatient}
            onClick={handleAssign}
          >
            Assigner
          </button>

          <button
            className="px-4 py-2 rounded text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-red-600 transition-colors"
            onClick={() => {
              if (
                window.confirm(
                  "Voulez-vous vraiment ignorer cet email ? Il ne sera plus proposé.",
                )
              )
                onIgnore();
            }}
          >
            Ignorer
          </button>
        </div>
      </div>
    </div>
  );
};
